import type { DesignBody } from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import { Button, DesignThumbnail } from '@/shared/ui';
import { QuoteDesignViewDialog } from './quote-design-view-dialog';

export interface QuoteDesignPreviewProps {
  readonly quoteRequestId: string;
  /** 견적 썸네일(worker 가 견적 사본으로 그린 그림) 서명 주소. 아직 없으면 null. */
  readonly thumbnailUrl: string | null;
  readonly designName: string;
  /** 견적 사본의 디자인 본문 — 디자인 보기 창이 이 본문을 그린다(뷰 조회는 본문을 다시 보내지 않는다). */
  readonly designBody: DesignBody;
}

/**
 * 디자인 사본 미리보기(견적 설계 §7-2) — 상품 상세 갤러리와 같은 각진 회색 정사각 칸에 견적 썸네일.
 * 아직 없거나 불러오지 못하면 "미리보기 준비 중". 아래 '디자인 보기'는 모든 면을 확대해 보는 창을 연다(뷰어 설계 V2) —
 * 창은 열려 있는 동안만 마운트되어 그때만 조회한다.
 */
export const QuoteDesignPreview = ({
  quoteRequestId,
  thumbnailUrl,
  designName,
  designBody,
}: QuoteDesignPreviewProps): ReactElement => {
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  return (
    <section aria-label="디자인 미리보기" className="flex flex-col gap-4">
      <DesignThumbnail url={thumbnailUrl} alt={designName} />
      <div className="flex items-baseline justify-between gap-4 border-t border-foreground/10 pt-4">
        <h2 className="text-[11px] leading-none font-semibold tracking-[0.18em] text-muted-foreground uppercase">
          Design<span className="sr-only"> 디자인</span>
        </h2>
        <p className="min-w-0 text-sm font-medium wrap-anywhere">{designName}</p>
      </div>
      <Button
        type="button"
        variant="outline"
        size="lg"
        className="w-full max-sm:h-11 sm:w-auto sm:self-start"
        onClick={() => setIsViewerOpen(true)}
      >
        디자인 보기
      </Button>
      {isViewerOpen ? (
        <QuoteDesignViewDialog
          quoteRequestId={quoteRequestId}
          designBody={designBody}
          onClose={() => setIsViewerOpen(false)}
        />
      ) : null}
    </section>
  );
};
